/**
 * AddTimeModal - Modal para añadir tiempo adicional a un turno
 * QUEUE-005: Permite a barberos añadir 5/10/15 minutos extra
 */

import { useState } from 'react';
import { X, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { QueueTicket } from '../../types';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { formatWaitTime } from '../../lib/timeUtils';

interface AddTimeModalProps {
  ticket: QueueTicket;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const TIME_OPTIONS = [
  { value: 5, label: '5 min' },
  { value: 10, label: '10 min' },
  { value: 15, label: '15 min' },
];

export function AddTimeModal({ ticket, isOpen, onClose, onSuccess }: AddTimeModalProps) {
  const [selectedMinutes, setSelectedMinutes] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleSubmit = async () => {
    if (!selectedMinutes) {
      toast.error('Selecciona la cantidad de minutos');
      return;
    }

    setIsSubmitting(true);

    try {
      const addWaitingTime = httpsCallable(functions, 'addWaitingTime');
      const result = await addWaitingTime({
        queueId: ticket.queueId,
        additionalMinutes: selectedMinutes,
        reason: reason.trim() || undefined,
      });

      const data = result.data as { success: boolean; newEstimatedTime: number };

      if (data.success) {
        setShowConfirmation(true);
        toast.success(`✅ ${selectedMinutes} min añadidos. Nuevo: ${data.newEstimatedTime} min`, { duration: 4000 });
        setTimeout(() => {
          setShowConfirmation(false);
          onSuccess?.();
          handleClose();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error adding time:', error);
      toast.error(error.message || 'Error al añadir tiempo');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedMinutes(null);
      setReason('');
      setShowConfirmation(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative bg-surface border border-hairline rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="p-6 border-b border-hairline">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-ink-subtle" aria-hidden="true" />
              <div>
                <h3 className="font-sans text-lg font-semibold text-ink">Añadir Tiempo</h3>
                <p className="text-sm text-ink-muted">#{ticket.ticketNumber}</p>
              </div>
            </div>
            <button onClick={handleClose} disabled={isSubmitting} className="p-2 text-ink-subtle hover:text-ink hover:bg-surface-2 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {showConfirmation && (
          <div className="absolute inset-0 bg-surface flex items-center justify-center z-10">
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" aria-hidden="true" />
              <p className="text-xl font-semibold text-ink">¡Tiempo añadido!</p>
            </div>
          </div>
        )}

        <div className="p-6 space-y-6">
          <div className="flex gap-3 p-4 bg-surface-2 border border-hairline rounded-lg">
            <AlertCircle className="w-5 h-5 text-ink-subtle shrink-0" aria-hidden="true" />
            <p className="text-sm text-ink-muted">Cliente notificado automáticamente</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-muted mb-3">¿Cuánto tiempo? *</label>
            <div className="grid grid-cols-3 gap-3">
              {TIME_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedMinutes(opt.value)}
                  disabled={isSubmitting}
                  className={`p-4 rounded-lg border transition-colors ${
                    selectedMinutes === opt.value
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-hairline bg-surface-2 text-ink-muted hover:border-hairline-strong hover:text-ink'
                  }`}
                >
                  <div className="text-2xl font-semibold tabular-nums">{opt.value}</div>
                  <div className="text-xs mt-1">min</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">Motivo (opcional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isSubmitting}
              placeholder="Servicio más complejo..."
              rows={3}
              className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
              maxLength={200}
            />
          </div>

          <div className="p-3 bg-surface-2 border border-hairline rounded-lg text-sm text-ink-muted">
            <div className="flex justify-between">
              <span>Actual:</span>
              <span className="font-semibold text-ink tabular-nums">{formatWaitTime(ticket.estimatedWaitTime || 0)}</span>
            </div>
            {selectedMinutes && (
              <div className="flex justify-between mt-2 pt-2 border-t border-hairline">
                <span>Nuevo:</span>
                <span className="font-semibold text-accent tabular-nums">{formatWaitTime((ticket.estimatedWaitTime || 0) + selectedMinutes)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 bg-surface-2 border-t border-hairline flex gap-3">
          <button onClick={handleClose} disabled={isSubmitting} className="flex-1 px-4 py-3 border border-hairline text-ink rounded-lg hover:bg-surface-3 transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedMinutes || isSubmitting}
            className="flex-1 px-4 py-3 bg-accent text-on-accent rounded-lg font-medium hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Añadiendo…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}
